import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { findNodeInTree, type NodesResponse } from '@/stores/scope';
import { formatEmployeeName } from '@/lib/format';
import { downloadAsCsv } from '@/lib/csv-export';

/** `/settings/notifications` (v0.4 §7 通知基盤、SettingsLayout「通知設定」タブ)。
 *  タブ1=チャネル有効/無効 (notification:manage, all_stores)。
 *  タブ2=配信タイミング設定 (tenant_config:view/manage、node_scalar_settings 経由の階層継承)。
 *  個人の購読ON/OFF (`/me/notification-subscriptions`) と連絡先登録はここではなく `/profile` に置く
 *  (本タブ自体が notification:manage 権限で SettingsLayout のタブ表示自体をゲートしており、
 *  一般スタッフは `/settings` 配下に到達できないため)。 */

type Channel = {
  channel_code: string;
  label: string;
  can_push: boolean;
  can_inbound: boolean;
  can_webview: boolean;
  is_billed: boolean;
  is_implemented: boolean;
  is_enabled: boolean;
};

function ChannelsSection() {
  const qc = useQueryClient();
  const { data: channels = [], isLoading } = useQuery<Channel[]>({
    queryKey: ['notification-channels'],
    queryFn: () => apiFetch<{ channels: Channel[] }>('/settings/notification-channels').then((r) => r.channels),
  });

  const toggleMut = useMutation({
    mutationFn: (v: { channel_code: string; is_enabled: boolean }) =>
      apiFetch('/settings/notification-channels', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notification-channels'] }); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-sub">通知を配信するチャネルを有効にします。未実装のチャネルは準備中のため有効化できません。</p>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="border border-surface-border rounded-lg bg-white divide-y divide-surface-line">
          {channels.map((ch) => (
            <div key={ch.channel_code} className="flex items-center justify-between gap-4 p-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-text-body">{ch.label}</span>
                  {!ch.is_implemented && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
                      準備中
                    </span>
                  )}
                  {ch.is_billed && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">
                      従量課金あり
                    </span>
                  )}
                </div>
                {ch.channel_code === 'email' && (
                  <p className="text-xs text-text-sub mt-1">メール送信には独自ドメインの設定が必要です（現在準備中）。</p>
                )}
              </div>
              <ToggleSwitch
                checked={ch.is_enabled}
                disabled={!ch.is_implemented || toggleMut.isPending}
                onChange={(v) => toggleMut.mutate({ channel_code: ch.channel_code, is_enabled: v })}
                ariaLabel={`${ch.label}を${ch.is_enabled ? '無効化' : '有効化'}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type SettingKey = 'shift_notice_timing' | 'shift_notice_delivery' | 'delivery_failure_mode' | 'agreement_36_expiry_notice';

const ALL_SETTING_KEYS: SettingKey[] = ['shift_notice_timing', 'shift_notice_delivery', 'delivery_failure_mode', 'agreement_36_expiry_notice'];
// v0.4 章④⑤ (Full限定) のシフト・36協定に関わる3設定はLiteテナントでは意味を持たないため隠す。
// delivery_failure_mode (配信失敗時の挙動) のみ両edition共通
const FULL_ONLY_SETTING_KEYS = new Set<SettingKey>(['shift_notice_timing', 'shift_notice_delivery', 'agreement_36_expiry_notice']);

const SETTING_LABELS: Record<SettingKey, string> = {
  shift_notice_timing: 'シフト提出締切のリマインド',
  shift_notice_delivery: 'シフト予定通知の配信方法',
  delivery_failure_mode: '配信失敗時の挙動',
  agreement_36_expiry_notice: '36協定 失効前のリマインド',
};

const SHIFT_DELIVERY_LABELS: Record<string, string> = { push: 'プッシュ通知 (即時に配信する)', pull: 'プル (アプリ内でのみ確認できる)' };
const FAILURE_MODE_LABELS: Record<string, string> = {
  auto_fallback: '自動フォールバック (他のチャネルで再送を試みる)',
  admin_notify_only: '管理者へ通知のみ',
  record_only: '記録のみ (追加の通知はしない)',
};

type SettingRowData = { setting_key: SettingKey; value: unknown; source_node_id: string | null; source_kind: InheritanceSourceKind };

function SettingRow({ row, nodeLabel, canClear, onSave, onClear, saving }: {
  row: SettingRowData;
  nodeLabel: string | null;
  canClear: boolean;
  onSave: (value: unknown) => void;
  onClear: () => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState(row.value);
  useEffect(() => { setDraft(row.value); }, [row.value]);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(row.value);

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium text-text-body">{SETTING_LABELS[row.setting_key]}</span>
        <InheritanceSourceBadge sourceKind={row.source_kind} sourceNodeLabel={nodeLabel} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {row.setting_key === 'shift_notice_delivery' && (
          <Select value={String(draft ?? 'push')} onValueChange={(v) => setDraft(v)}>
            <SelectTrigger aria-label={SETTING_LABELS[row.setting_key]} className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(SHIFT_DELIVERY_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {row.setting_key === 'delivery_failure_mode' && (
          <Select value={String(draft ?? 'auto_fallback')} onValueChange={(v) => setDraft(v)}>
            <SelectTrigger aria-label={SETTING_LABELS[row.setting_key]} className="w-72"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(FAILURE_MODE_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {(row.setting_key === 'shift_notice_timing' || row.setting_key === 'agreement_36_expiry_notice') && (
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={row.setting_key === 'shift_notice_timing' ? 30 : 90}
              value={typeof draft === 'number' ? draft : ''}
              onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
              aria-label={SETTING_LABELS[row.setting_key]}
              className="w-20 min-h-[44px] appearance-none rounded border border-surface-border px-2.5 py-2 text-sm focus-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="text-sm text-text-sub">日前</span>
          </div>
        )}
        <Button variant="secondary" size="sm" disabled={!isDirty || draft === '' || saving} loading={saving}
          onClick={() => onSave(draft)}>
          保存
        </Button>
        {canClear && (
          <Button variant="ghost" size="sm" disabled={saving} onClick={onClear}>
            既定に戻す
          </Button>
        )}
      </div>
    </div>
  );
}

function TimingSection() {
  const user = useAuthStore((s) => s.user);
  const isFull = user?.edition !== 'lite';
  const applicableKeys = ALL_SETTING_KEYS.filter((k) => isFull || !FULL_ONLY_SETTING_KEYS.has(k));
  const qc = useQueryClient();

  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    staleTime: 5 * 60 * 1000,
  });
  const roots = nodesData?.roots ?? [];

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedNodeId && roots.length > 0) setSelectedNodeId(roots[0]!.node_id);
  }, [roots, selectedNodeId]);
  const isRootSelected = !!selectedNodeId && roots.some((r) => r.node_id === selectedNodeId);
  const [saveAsTenantDefault, setSaveAsTenantDefault] = useState(false);
  useEffect(() => { if (!isRootSelected) setSaveAsTenantDefault(false); }, [isRootSelected]);

  const { data, isLoading } = useQuery<{ node_id: string; settings: SettingRowData[] }>({
    queryKey: ['notification-settings', selectedNodeId],
    queryFn: () => apiFetch(`/settings/notification-settings?node_id=${encodeURIComponent(selectedNodeId!)}`),
    enabled: !!selectedNodeId,
  });
  const settings = (data?.settings ?? []).filter((s) => applicableKeys.includes(s.setting_key));

  const saveMut = useMutation({
    mutationFn: (v: { setting_key: SettingKey; value: unknown }) => apiFetch('/settings/notification-settings', {
      method: 'PUT',
      body: JSON.stringify({ node_id: saveAsTenantDefault ? null : selectedNodeId, setting_key: v.setting_key, value: v.value }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notification-settings'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const clearMut = useMutation({
    mutationFn: (settingKey: SettingKey) => apiFetch('/settings/notification-settings', {
      method: 'PUT',
      body: JSON.stringify({ node_id: selectedNodeId, setting_key: settingKey, value: null }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notification-settings'] }); toast.success('既定値に戻しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '操作に失敗しました'),
  });

  const handleSingleNodeChange = (next: Set<string>) => {
    if (next.size === 0) { setSelectedNodeId(null); return; }
    const arr = [...next];
    const newOne = arr.find((id) => id !== selectedNodeId);
    setSelectedNodeId(newOne ?? arr[0]!);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-sub">
        店舗・ブランドごとに通知のタイミングを上書きできます。上書きしないノードは上位ノード (最終的にはテナント既定) の値を引き継ぎます。
      </p>
      <div className="max-w-xs">
        <label className="block text-xs text-text-sub mb-1">対象ノード</label>
        <NodeMultiSelect
          nodes={roots}
          selectedIds={selectedNodeId ? new Set([selectedNodeId]) : new Set()}
          onChange={handleSingleNodeChange}
        />
      </div>

      {isRootSelected && (
        <label className="flex items-center gap-2 text-sm min-h-[44px]">
          <input type="checkbox" className="w-5 h-5 accent-brand-red" checked={saveAsTenantDefault}
            onChange={(e) => setSaveAsTenantDefault(e.target.checked)} />
          この変更をテナント全体の既定値として保存する (組織階層に依らず全ノードへ適用)
        </label>
      )}

      {!selectedNodeId ? (
        <p className="text-sm text-text-sub">対象ノードを選択してください。</p>
      ) : isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="border border-surface-border rounded-lg bg-white divide-y divide-surface-line">
          {settings.map((row) => (
            <SettingRow
              key={row.setting_key}
              row={row}
              nodeLabel={row.source_node_id ? findNodeInTree(roots, row.source_node_id)?.name ?? null : null}
              canClear={row.source_kind === 'self'}
              saving={saveMut.isPending || clearMut.isPending}
              onSave={(value) => saveMut.mutate({ setting_key: row.setting_key, value })}
              onClear={() => clearMut.mutate(row.setting_key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type LineCredentialLabel = 'channel_secret' | 'channel_access_token' | 'line_login_channel_secret';
type LineCredentialMeta = { label: LineCredentialLabel; registered_at: string; rotated_at: string | null };
type LineChannelConfig = {
  messaging_channel_id: string | null;
  line_login_channel_id: string | null;
  liff_id: string | null;
  credentials: LineCredentialMeta[];
};

function credentialStatusLabel(credentials: LineCredentialMeta[], label: LineCredentialLabel): string {
  const cred = credentials.find((c) => c.label === label);
  if (!cred) return '未登録';
  const ts = new Date(cred.rotated_at ?? cred.registered_at).toLocaleString('ja-JP');
  return `登録済み (最終更新: ${ts})`;
}

/** v0.4 §7-6 (L-4): テナント別LINEチャネル設定 (notification:manage、all_stores。タブ表示自体が
 *  同権限でゲートされているため本コンポーネント側での追加チェックは行わない、チャネルタブと同型)。
 *  チャネルシークレット・アクセストークンはバックエンドが実値を一切返さない (書込専用) ため、
 *  常に空欄始まりで「変更する場合のみ入力」とし、登録有無/最終更新のみ表示する。 */
function LineChannelSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<LineChannelConfig>({
    queryKey: ['line-channel-settings'],
    queryFn: () => apiFetch<LineChannelConfig>('/settings/line-channel'),
  });

  const [messagingChannelId, setMessagingChannelId] = useState('');
  const [lineLoginChannelId, setLineLoginChannelId] = useState('');
  const [liffId, setLiffId] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [channelAccessToken, setChannelAccessToken] = useState('');
  const [lineLoginChannelSecret, setLineLoginChannelSecret] = useState('');

  useEffect(() => {
    if (!data) return;
    setMessagingChannelId(data.messaging_channel_id ?? '');
    setLineLoginChannelId(data.line_login_channel_id ?? '');
    setLiffId(data.liff_id ?? '');
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => {
      // チャネルシークレット/アクセストークンは入力があった場合のみキー自体を含める
      // (空文字を送るとサーバー側で意図せずローテーションになる/ならない、を確実に分けるため)
      const body: Record<string, unknown> = {
        messaging_channel_id: messagingChannelId.trim() || null,
        line_login_channel_id: lineLoginChannelId.trim() || null,
        liff_id: liffId.trim() || null,
      };
      if (channelSecret.trim()) body.channel_secret = channelSecret.trim();
      if (channelAccessToken.trim()) body.channel_access_token = channelAccessToken.trim();
      if (lineLoginChannelSecret.trim()) body.line_login_channel_secret = lineLoginChannelSecret.trim();
      return apiFetch('/settings/line-channel', { method: 'PUT', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      setChannelSecret('');
      setChannelAccessToken('');
      setLineLoginChannelSecret('');
      qc.invalidateQueries({ queryKey: ['line-channel-settings'] });
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const credentials = data?.credentials ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-sub">
        LINE公式アカウントと連携すると、シフト確定・締切リマインド等をLINEでも受け取れるようになります。設定にはLINE Developersコンソールでの事前準備が必要です。
      </p>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="border border-surface-border rounded-lg bg-white p-4 space-y-4">
          <div>
            <label htmlFor="line-messaging-channel-id" className="block text-xs text-text-sub mb-1">Messaging APIチャネルID</label>
            <input id="line-messaging-channel-id" value={messagingChannelId} maxLength={100}
              onChange={(e) => setMessagingChannelId(e.target.value)}
              className="w-full min-h-[44px] rounded border border-surface-border px-3 py-2 text-sm focus-ring" />
          </div>
          <div>
            <label htmlFor="line-login-channel-id" className="block text-xs text-text-sub mb-1">LINEログインチャネルID</label>
            <input id="line-login-channel-id" value={lineLoginChannelId} maxLength={100}
              onChange={(e) => setLineLoginChannelId(e.target.value)}
              className="w-full min-h-[44px] rounded border border-surface-border px-3 py-2 text-sm focus-ring" />
          </div>
          <div>
            <label htmlFor="line-liff-id" className="block text-xs text-text-sub mb-1">LIFF ID</label>
            <input id="line-liff-id" value={liffId} maxLength={100}
              onChange={(e) => setLiffId(e.target.value)}
              className="w-full min-h-[44px] rounded border border-surface-border px-3 py-2 text-sm focus-ring" />
          </div>

          <div className="pt-3 border-t border-surface-line space-y-4">
            <p className="text-xs text-text-sub">
              チャネルシークレット・チャネルアクセストークンはLINE Developersコンソールで発行できます。一度保存すると値は再表示されません（再度確認する場合はLINE Developersコンソールをご覧ください）。
              <span className="block mt-1 text-alert-text">
                ⚠️ Messaging API用とLINEログイン用は別のチャネル・別のシークレットです。取り違えるとLINE通知の送受信が壊れます。
              </span>
            </p>
            <div>
              <p className="text-xs font-semibold text-text-main mb-2">Messaging API用（LINE公式アカウントの通知送受信）</p>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <label htmlFor="line-channel-secret" className="text-xs text-text-sub">チャネルシークレット</label>
                    <span className="text-xs text-text-sub">{credentialStatusLabel(credentials, 'channel_secret')}</span>
                  </div>
                  <input id="line-channel-secret" type="password" value={channelSecret} maxLength={200} autoComplete="off"
                    placeholder="変更する場合のみ入力" onChange={(e) => setChannelSecret(e.target.value)}
                    className="w-full min-h-[44px] rounded border border-surface-border px-3 py-2 text-sm font-mono focus-ring" />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <label htmlFor="line-channel-access-token" className="text-xs text-text-sub">チャネルアクセストークン</label>
                    <span className="text-xs text-text-sub">{credentialStatusLabel(credentials, 'channel_access_token')}</span>
                  </div>
                  <input id="line-channel-access-token" type="password" value={channelAccessToken} maxLength={500} autoComplete="off"
                    placeholder="変更する場合のみ入力" onChange={(e) => setChannelAccessToken(e.target.value)}
                    className="w-full min-h-[44px] rounded border border-surface-border px-3 py-2 text-sm font-mono focus-ring" />
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-surface-line">
              <p className="text-xs font-semibold text-text-main mb-2">LINEログイン用（従業員の初回LINE連携）</p>
              <div>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <label htmlFor="line-login-channel-secret" className="text-xs text-text-sub">LINEログインチャネルシークレット</label>
                  <span className="text-xs text-text-sub">{credentialStatusLabel(credentials, 'line_login_channel_secret')}</span>
                </div>
                <input id="line-login-channel-secret" type="password" value={lineLoginChannelSecret} maxLength={200} autoComplete="off"
                  placeholder="変更する場合のみ入力" onChange={(e) => setLineLoginChannelSecret(e.target.value)}
                  className="w-full min-h-[44px] rounded border border-surface-border px-3 py-2 text-sm font-mono focus-ring" />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button variant="primary" size="md" disabled={saveMut.isPending} loading={saveMut.isPending}
              onClick={() => saveMut.mutate()}>
              保存
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 使い勝手フィードバック (2026-09-04、D-U6): 管理者が「どの社員がLINE連携できているか」を確認し、
 *  未連携者への督促に使う一覧・エクスポート画面。本人向け (`/profile` の `LineLinkSection`) とは別。
 *  ⚠️ 社員マスタは既存 `GET /employees` (employees.ts、SCD-2 の最新版解決ロジックを持つ) をそのまま
 *  再利用し、氏名解決ロジックをここで複製しない (§8-55型を避ける)。連携状況は新設 `GET /line/links`
 *  (`notification:manage`・本タブと同じ権限、line_user_id 自体は返さない、D-492と同じ最小化方針)。
 *  `GET /employees` は isCompanyAdmin 限定 (role ベース) のため、notification:manage を持つが
 *  company_admin ではないカスタムロールでは 403 になりうる — その場合は専用メッセージで案内する。 */
type EmployeeSummary = {
  employee_id: string; family_name: string; given_name: string; middle_name: string | null;
  employee_number: string; status: string;
};
type LineLinksResponse = { links: { employee_id: string; linked_at: string }[] };

function LineLinkStatusSection() {
  const employeesQuery = useQuery<EmployeeSummary[]>({ queryKey: ['employees'], queryFn: () => apiFetch<EmployeeSummary[]>('/employees') });
  const linksQuery = useQuery<LineLinksResponse>({ queryKey: ['line-links'], queryFn: () => apiFetch<LineLinksResponse>('/line/links') });
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(false);

  const rows = useMemo(() => {
    const linkedAtByEmployee = new Map((linksQuery.data?.links ?? []).map((l) => [l.employee_id, l.linked_at]));
    return (employeesQuery.data ?? [])
      .filter((e) => e.status === 'active')
      .map((e) => ({
        employee_id: e.employee_id,
        name: formatEmployeeName(e),
        employee_number: e.employee_number,
        linked_at: linkedAtByEmployee.get(e.employee_id) ?? null,
      }))
      .sort((a, b) => a.employee_number.localeCompare(b.employee_number));
  }, [employeesQuery.data, linksQuery.data]);

  const visibleRows = showUnlinkedOnly ? rows.filter((r) => !r.linked_at) : rows;
  const unlinkedCount = rows.filter((r) => !r.linked_at).length;

  const exportCsv = () => {
    downloadAsCsv(
      'line連携状況.csv',
      ['社員番号', '氏名', '連携状況', '連携日時'],
      visibleRows.map((r) => ({
        社員番号: r.employee_number, 氏名: r.name,
        連携状況: r.linked_at ? '連携済み' : '未連携', 連携日時: r.linked_at ?? '',
      })),
    );
  };

  if (employeesQuery.isError) {
    const message = employeesQuery.error instanceof ApiClientError && employeesQuery.error.status === 403
      ? 'この一覧を見るには会社管理者権限が必要です。'
      : '社員情報の取得に失敗しました。';
    return <div className="border border-surface-border rounded-lg bg-white p-4 text-sm text-danger-text">{message}</div>;
  }
  if (linksQuery.isError) {
    return <div className="border border-surface-border rounded-lg bg-white p-4 text-sm text-danger-text">LINE連携状況の取得に失敗しました。</div>;
  }

  return (
    <div className="border border-surface-border rounded-lg bg-white">
      <div className="p-3 sm:p-4 border-b border-surface-border space-y-1">
        <h3 className="text-sm font-semibold text-text-body">従業員のLINE連携状況</h3>
        <p className="text-xs text-text-sub">在籍中の従業員のうち、LINEアカウント連携が未完了の人を確認できます。督促の際にご利用ください。</p>
      </div>
      {employeesQuery.isLoading || linksQuery.isLoading ? (
        <div className="p-4 text-sm text-text-sub">読み込み中…</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-sm text-text-sub">在籍中の従業員がいません。</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2">
            <label className="flex items-center gap-2 text-sm text-text-body">
              {/* design-expertレビュー反映: input自体のタップ領域が16pxしかなかった (labelのmin-h-[44px]は
                  ヒットエリアを広げない)。LiteDataBrowsePage.tsx と同じ w-11 h-11 -m-3 のラップで44px相当にする */}
              <span className="inline-flex items-center justify-center w-11 h-11 -m-3 cursor-pointer">
                <input type="checkbox" checked={showUnlinkedOnly} onChange={(e) => setShowUnlinkedOnly(e.target.checked)}
                  className="w-4 h-4 align-middle" />
              </span>
              未連携のみ表示 ({unlinkedCount}人)
            </label>
            <Button variant="secondary" size="sm" leftIcon={<Download size={14} />} onClick={exportCsv} disabled={visibleRows.length === 0}>
              CSVエクスポート
            </Button>
          </div>
          {visibleRows.length === 0 ? (
            <div className="p-4 text-sm text-text-sub">該当する従業員がいません。</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t border-surface-border bg-surface-sunken">
                    <th scope="col" className="text-left px-3 sm:px-4 py-2 font-semibold text-text-body whitespace-nowrap">社員番号</th>
                    <th scope="col" className="text-left px-3 sm:px-4 py-2 font-semibold text-text-body">氏名</th>
                    <th scope="col" className="text-left px-3 sm:px-4 py-2 font-semibold text-text-body whitespace-nowrap">連携状況</th>
                    <th scope="col" className="text-left px-3 sm:px-4 py-2 font-semibold text-text-body whitespace-nowrap">連携日時</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {visibleRows.map((r) => (
                    <tr key={r.employee_id} className="hover:bg-surface-sunken">
                      <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-text-sub">{r.employee_number}</td>
                      <td className="px-3 sm:px-4 py-2 break-words">{r.name}</td>
                      <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                        {r.linked_at ? (
                          // design-expertレビュー反映: misespark-navy はロゴタイプ専用の予約トークンのため、
                          // 他画面の「連携済み/完了」系バッジと同じ success.* トークンに統一する
                          <span className="text-xs px-2 py-0.5 rounded-full bg-success-bg text-success-text border border-success-border">連携済み</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-surface-sunken text-text-sub border border-surface-border">未連携</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-text-sub">{r.linked_at ? new Date(r.linked_at).toLocaleString('ja-JP') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function NotificationsSettingsPage() {
  const [tab, setTab] = useState<'channels' | 'timing' | 'line'>('channels');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">通知設定</h2>
        <p className="text-sm text-text-sub mt-1">通知チャネルの有効/無効と、配信タイミングを管理します</p>
      </div>
      <div className="border-b border-surface-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="通知設定タブ">
          {([
            { key: 'channels' as const, label: 'チャネル' },
            { key: 'timing' as const, label: '配信タイミング' },
            { key: 'line' as const, label: 'LINE連携' },
          ]).map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              aria-current={tab === t.key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === t.key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>
      {tab === 'channels' ? <ChannelsSection /> : tab === 'timing' ? <TimingSection /> : (
        <div className="space-y-4">
          <LineChannelSection />
          <LineLinkStatusSection />
        </div>
      )}
    </div>
  );
}
